import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="latency-recording"
// Manages latency recording timer for measuring student response time
export default class extends Controller {
  static targets = [
    "instructionButton",
    "responseButton",
    "resetButton",
    "timer",
    "currentLatency",
    "statistics",
    "instructionTimeField",
    "latencySecondsField",
    "dataField"
  ]

  static values = {
    lessonSkillId: Number
  }

  connect() {
    this.isRunning = false
    this.startTime = null
    this.elapsedSeconds = 0
    this.timerInterval = null

    // Load existing data from hidden field if present
    this.loadFromHiddenField()

    console.log("Latency recording controller connected")
  }

  disconnect() {
    this.stopTimer()
  }

  // Start latency timer when instruction is given
  startInstruction(event) {
    event.preventDefault()

    if (this.isRunning) {
      return
    }

    this.isRunning = true
    this.startTime = new Date()
    this.elapsedSeconds = 0

    // Store instruction time in hidden field
    if (this.hasInstructionTimeFieldTarget) {
      this.instructionTimeFieldTarget.value = this.startTime.toISOString()
    }

    // Start timer display
    this.startTimer()

    // Update UI
    this.updateButtonStates()
    this.flashVisualSignal('instruction')
    this.playSound('instruction')
  }

  // Stop latency timer when student responds
  recordResponse(event) {
    event.preventDefault()

    if (!this.isRunning) {
      return
    }

    this.isRunning = false
    const responseTime = new Date()
    const latencySeconds = Math.floor((responseTime - this.startTime) / 1000)

    // Store latency in hidden field
    if (this.hasLatencySecondsFieldTarget) {
      this.latencySecondsFieldTarget.value = latencySeconds
    }

    // Stop timer
    this.stopTimer()

    // Update current latency display
    if (this.hasCurrentLatencyTarget) {
      this.currentLatencyTarget.textContent = this.formatTime(latencySeconds)
    }

    // Update statistics
    this.updateStatistics(latencySeconds)
    this.saveToHiddenField()

    // Update UI
    this.updateButtonStates()
    this.flashVisualSignal('response')
    this.playSound('response')
  }

  // Reset latency timer
  resetTimer(event) {
    event.preventDefault()

    this.stopTimer()
    this.isRunning = false
    this.startTime = null
    this.elapsedSeconds = 0

    // Clear hidden fields
    if (this.hasInstructionTimeFieldTarget) {
      this.instructionTimeFieldTarget.value = ''
    }
    if (this.hasLatencySecondsFieldTarget) {
      this.latencySecondsFieldTarget.value = ''
    }

    // Reset display
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = '00:00'
    }
    if (this.hasCurrentLatencyTarget) {
      this.currentLatencyTarget.textContent = 'N/A'
    }

    this.updateButtonStates()
  }

  // Start the timer interval
  startTimer() {
    this.timerInterval = setInterval(() => {
      this.elapsedSeconds++
      this.updateTimerDisplay()
    }, 1000)
  }

  // Stop the timer interval
  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  // Update timer display
  updateTimerDisplay() {
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = this.formatTime(this.elapsedSeconds)
    }
  }

  // Format seconds to MM:SS
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  // Update button states based on timer status
  updateButtonStates() {
    if (this.hasInstructionButtonTarget) {
      this.instructionButtonTarget.disabled = this.isRunning
    }

    if (this.hasResponseButtonTarget) {
      this.responseButtonTarget.disabled = !this.isRunning
    }

    if (this.hasResetButtonTarget) {
      this.resetButtonTarget.disabled = this.isRunning
    }
  }

  // Update statistics display
  updateStatistics(newLatency) {
    if (!this.hasStatisticsTarget) return

    // Get existing measurements from the display or initialize
    const measurements = this.getMeasurements()
    measurements.push(newLatency)

    // Calculate statistics
    const total = measurements.length
    const sum = measurements.reduce((a, b) => a + b, 0)
    const average = (sum / total).toFixed(2)
    const min = Math.min(...measurements)
    const max = Math.max(...measurements)

    // Update display
    this.statisticsTarget.innerHTML = `
      <div class="row text-center">
        <div class="col-3">
          <div class="stat-label text-muted small">Total Measurements</div>
          <div class="stat-value h5 mb-0">${total}</div>
        </div>
        <div class="col-3">
          <div class="stat-label text-muted small">Average</div>
          <div class="stat-value h5 mb-0 text-primary">${this.formatTime(Math.round(average))}</div>
        </div>
        <div class="col-3">
          <div class="stat-label text-muted small">Min</div>
          <div class="stat-value h5 mb-0 text-success">${this.formatTime(min)}</div>
        </div>
        <div class="col-3">
          <div class="stat-label text-muted small">Max</div>
          <div class="stat-value h5 mb-0 text-warning">${this.formatTime(max)}</div>
        </div>
      </div>
    `

    // Store measurements for future updates
    this.storeMeasurements(measurements)
  }

  // Get stored measurements from session storage
  getMeasurements() {
    const key = `latency_measurements_${this.lessonSkillIdValue}`
    const stored = sessionStorage.getItem(key)
    return stored ? JSON.parse(stored) : []
  }

  // Store measurements in session storage
  storeMeasurements(measurements) {
    const key = `latency_measurements_${this.lessonSkillIdValue}`
    sessionStorage.setItem(key, JSON.stringify(measurements))
  }

  // Save latency data to hidden field for form submission
  saveToHiddenField() {
    if (!this.hasDataFieldTarget) return

    const measurements = this.getMeasurements()
    if (measurements.length === 0) return

    const total = measurements.length
    const sum = measurements.reduce((a, b) => a + b, 0)
    const average = (sum / total).toFixed(2)
    const min = Math.min(...measurements)
    const max = Math.max(...measurements)

    const data = {
      measurements: measurements,
      total: total,
      average: parseFloat(average),
      min: min,
      max: max
    }

    this.dataFieldTarget.value = JSON.stringify(data)
  }

  // Load latency data from hidden field on page load
  loadFromHiddenField() {
    if (!this.hasDataFieldTarget) return
    if (!this.dataFieldTarget.value) return

    try {
      const data = JSON.parse(this.dataFieldTarget.value)
      const measurements = data.measurements || []

      if (measurements.length > 0) {
        // Store measurements in session storage
        this.storeMeasurements(measurements)

        // Update statistics display
        const total = data.total || measurements.length
        const average = data.average || (measurements.reduce((a, b) => a + b, 0) / total).toFixed(2)
        const min = data.min || Math.min(...measurements)
        const max = data.max || Math.max(...measurements)

        if (this.hasStatisticsTarget) {
          this.statisticsTarget.innerHTML = `
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div class="text-xs" style="color:var(--color-gray-400)">Total Measurements</div>
                <div class="text-lg font-bold">${total}</div>
              </div>
              <div>
                <div class="text-xs" style="color:var(--color-gray-400)">Average</div>
                <div class="text-lg font-bold" style="color:var(--color-primary)">${this.formatTime(Math.round(average))}</div>
              </div>
              <div>
                <div class="text-xs" style="color:var(--color-gray-400)">Min</div>
                <div class="text-lg font-bold" style="color:var(--color-success)">${this.formatTime(min)}</div>
              </div>
              <div>
                <div class="text-xs" style="color:var(--color-gray-400)">Max</div>
                <div class="text-lg font-bold" style="color:var(--color-warning-hover)">${this.formatTime(max)}</div>
              </div>
            </div>
          `
        }
      }
    } catch (error) {
      console.error('Error loading latency data:', error)
    }
  }

  // Flash visual signal
  flashVisualSignal(type) {
    const colors = {
      instruction: 'rgba(0, 123, 255, 0.3)',  // Blue for instruction
      response: 'rgba(40, 167, 69, 0.3)'      // Green for response
    }

    const overlay = document.createElement('div')
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${colors[type] || colors.instruction};
      pointer-events: none;
      z-index: 9999;
      animation: fadeOut 0.5s ease-out;
    `

    document.body.appendChild(overlay)
    setTimeout(() => overlay.remove(), 500)
  }

  // Play sound notification
  playSound(type) {
    // Create audio context for beep
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Different frequencies for different events
      oscillator.frequency.value = type === 'instruction' ? 800 : 1000
      oscillator.type = 'sine'

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.2)
    } catch (error) {
      console.warn('Audio not supported:', error)
    }
  }
}
